import { LocationChangeEvent, PlatformLocation } from '@angular/common';
import { Injectable, Type } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { findLast, findLastIndex } from 'lodash';
import { filter, map, ReplaySubject } from 'rxjs';
import { FrameworkService } from './framework.service';
import { NavigatorService } from './navigator.service';
import { SysEventsService } from './sysevents.service';
import { SysGlobals } from './sysglobals';

export class HistoryItem {
    Id: number;
    Url: string;
    QueryParams: any;
    Title: string;
    Component: Type<any>;
    ComponentName: string;
    IsWindow: boolean;
}

@Injectable()
export class RouteHistoryService {

    private _history: HistoryItem[] = [];

    private _pageHistorySubject = new ReplaySubject<HistoryItem>(1);
    readonly PageHistory$ = this._pageHistorySubject.asObservable();

    private _historyItemsSubject = new ReplaySubject<HistoryItem[]>(1);
    readonly HistoryItems$ = this._historyItemsSubject.asObservable();

    get HistoryLength() { return this._history.length; }

    constructor(private router: Router,
        private activatedRoute: ActivatedRoute,
        private navigatorService: NavigatorService,
        private sysEventsService: SysEventsService,
        private frameworkService: FrameworkService,
        private location: PlatformLocation) {
        this.sysEventsService.OnNavigateDefault.subscribe((result) => {
            if (!result) return;
            this.ClearHistory(0);
        });
        this.location.onPopState(this.OnBrowserButtonClick);
    }

    // on after init application start saving route history
    public StartSaveHistory() {
        this.router.events
            .pipe(
                // filter events, take only NavigationEnd
                filter(event => event instanceof NavigationEnd),
                map(() => {
                    let { route, url } = this.GetCurrentRouteWithUrl();
                    // create history item entity
                    return {
                        Id: this.router.getCurrentNavigation().id,
                        Url: url,
                        QueryParams: this.GetQueryParamsWithoutPrefixes(route.snapshot.queryParams),
                        Component: route.component,
                        IsWindow: route.snapshot.outlet === 'window'
                    } as HistoryItem;
                }),
                filter(historyItem => !historyItem.Url.includes('system/')))
            // send it to the page where the title will be set
            .subscribe(historyItem => this.SaveHistoryItem(historyItem));
    }

    SetHistoryItemTitle(componentName: string, title: string) {
        // get history item index by component name
        const historyItemIndex = findLastIndex(this._history, x => x.ComponentName === componentName);
        if (historyItemIndex < 0) return;

        // set history item title
        this._history[historyItemIndex].Title = title;
        // share history to the subscribers
        this.ShareHistory();
    }

    UpdateHistoryItem(historyItem: HistoryItem) {
        const historyItemIndex = this._history.findIndex(x => x.Id === historyItem.Id);
        if (historyItemIndex < 0) return;

        this._history[historyItemIndex] = historyItem;

        // share history to the subscribers
        this.ShareHistory();
    }

    public SaveHistoryItem(historyItem: HistoryItem) {
        // remove 'new' route from history for correct navigation through history
        const lastHistoryItem = this._history.length && this._history[this._history.length - 1];
        if (lastHistoryItem && lastHistoryItem.Url.endsWith('new')) {
            this._history.pop();
        }

        // if there is an item already just update the title, if not - add it to the history 
        const historyItemIndex = this._history.findIndex(x => x.Id === historyItem.Id);
        if (historyItemIndex < 0) {
            this._history.push(historyItem);
        }
        // share history to the subscribers
        this.ShareHistory();

        this._pageHistorySubject.next(historyItem);
    }

    public GoBack() {
        // get current history item from history
        const currentHistoryItem = this._history.length && this._history[this._history.length - 1];
        if (!currentHistoryItem) {
            // if there is no history - navigate default
            return this.frameworkService.NavigateDefault();
        }

        // get previous history item
        const previousHistoryItem = this._history[this._history.length - 2];
        if (previousHistoryItem) {
            // if going back from window to the full page - navigate null to avoid page reload
            if (currentHistoryItem.IsWindow && !previousHistoryItem.IsWindow) {
                return this.GoBackFromWindow(previousHistoryItem, this._history.length - 2);
            }
            return this.NavigateItem(previousHistoryItem, this._history.length - 2);
        }
        // if there is no history - navigate default
        return this.frameworkService.NavigateDefault();
    }

    public NavigateHistoryItem(item: HistoryItem) {
        // get history item by id
        const historyItem = this._history.find(x => x.Id === item.Id);
        const historyItemIndex = this._history.indexOf(historyItem);
        // get current history item from history
        const currentHistoryItem = this._history[this._history.length - 1];
        if (currentHistoryItem.IsWindow) {
            // get full page url
            const { _, url } = this.GetCurrentRouteWithUrl(true);
            // if current route full page url equals navigatable item full page url
            if (historyItem.Url == url) {
                // just close the window to avoid page reload
                return this.NavigateItem(null, historyItemIndex);
            }
        }
        return this.NavigateItem(historyItem, historyItemIndex);
    }

    CloseWindow() {
        // get current history item from history
        const currentHistoryItem = this._history.length && this._history[this._history.length - 1];
        if (!currentHistoryItem) {
            // if there is no history - navigate default
            return this.frameworkService.NavigateDefault();
        }
        // get last history item that is not a window
        const lastPageHistoryItem = findLast(this._history, x => !x.IsWindow);
        // get last page index or previous
        const lastPageHistoryItemIndex = lastPageHistoryItem ? this._history.indexOf(lastPageHistoryItem) : this._history.length - 2;

        return this.GoBackFromWindow(lastPageHistoryItem, lastPageHistoryItemIndex);
    }

    private GoBackFromWindow(pageHistoryItem: HistoryItem, cleanStartIndex?: number) {
        const { _, url } = this.GetCurrentRouteWithUrl(true);
        // if current primary page is not the same take page history item
        const navigationPath = pageHistoryItem?.Url === url ? null : pageHistoryItem;
        return this.NavigateItem(navigationPath, cleanStartIndex);
    }

    private NavigateItem(historyItem: HistoryItem, cleanStartIndex?: number) {
        // navigate to the previous history item or close window without reload
        return (historyItem && this.navigatorService.Navigate([historyItem.Url], historyItem.QueryParams) || this.navigatorService.Navigate(null))
            .then((result) => {
                // if navigatation was successful
                if (result) {
                    // clean history if needed
                    if (cleanStartIndex != null) this.ClearHistory(cleanStartIndex);
                    // share history to the subscribers
                    this.ShareHistory();
                    return Promise.resolve(true);
                }
                return Promise.resolve(false);
            });
    }

    private ShareHistory() {
        // get history without current item to show it correctly
        let historyWithoutCurrent = [...this._history];
        if (this._history.length > 0) historyWithoutCurrent.splice(this._history.length - 1);

        // remove first item in case our primary route is the same (for windows)
        let { _, url } = this.GetCurrentRouteWithUrl(true);
        if (historyWithoutCurrent.length > 0 && historyWithoutCurrent[0].Url === url) {
            historyWithoutCurrent.shift();
        }

        // send history withput current the subscribers (breadcrumb)
        this._historyItemsSubject.next(historyWithoutCurrent);
    }

    public HasHistory() {
        // check if there are history items
        return this._history.length > 0;
    }

    public ClearHistory(startIndex?: number) {
        // clear history at all or by the index (on breadcrumb item click)
        if (startIndex != null) {
            // clean history from item to the prelast item (because last item is correct and needed in history after navigation)
            this.ClearPagesState(startIndex, this._history.length - 1);
            this._history.splice(startIndex, this._history.length - startIndex - 1);
        } else {
            this.ClearPagesState();
            this._history.length = 0;
        }
        // send history withput current the subscribers (breadcrumb)
        this._historyItemsSubject.next(this._history);
    }

    private ClearPagesState(startIndex?: number, endIndex?: number) {
        for (let i = startIndex || 0; i < (endIndex != null ? endIndex : this._history.length); i++) {
            this.ClearPageState(this._history[i]);
        }
    }

    private ClearPageState(historyItem: HistoryItem) {
        const pageComponentName = historyItem && historyItem.ComponentName;
        // if no page component name or there is the same component in history - no need to clear it
        if (!pageComponentName || this._history.some(x => x.Id !== historyItem.Id && x.Component === historyItem.Component)) return;

        delete SysGlobals.PageStates[pageComponentName];
    }

    private OnBrowserButtonClick = (event: LocationChangeEvent) => {
        const navigationId = event.state.navigationId;
        const historyItemIndex = this._history.findIndex(x => x.Id === navigationId);
        if (historyItemIndex >= 0) {
            this.ClearHistory(historyItemIndex);
        }
    }

    private GetCurrentRouteWithUrl(onlyPrimaryOutlet?: boolean): any {
        let route = this.activatedRoute;
        let urlSegments = route.snapshot.url;
        // get the bottom route and url by all the tree
        // if onlyParentOutlet = true then it makes search by firstChild which can't be a window
        while (onlyPrimaryOutlet ? route.firstChild : route.children.length) {
            route = onlyPrimaryOutlet ? route.firstChild : route.children[route.children.length - 1];
            urlSegments = [...urlSegments, ...route.snapshot.url];
        }
        return { route: route, url: urlSegments.map(x => x.path).join('/') }
    }

    private GetQueryParamsWithoutPrefixes(params) {
        let newParams = {};
        for (let key in params) {
            const newKey = key.substring(key.indexOf('_') + 1);
            newParams[newKey] = params[key];
        }
        return newParams;
    }

    HasHistoryForWindow() {
        if (!this._history || this._history.length < 2) return false;

        const currentHistoryItem = this._history[this._history.length - 1];
        const previousHistoryItem = this._history[this._history.length - 2];
        return !(!previousHistoryItem.IsWindow && currentHistoryItem.IsWindow);
    }
}